/**
 * Identity status label + blocked-flag registry (F1 M2 identity slice).
 *
 * DD data model (`docs/architecture/design/DD-task-first-f1-m2-identity-fe.md`, "Data model
 * (client-side types)") specifies this as `Record<NaturalPersonIdentityStatus, ...>`, using the
 * codegen-generated enum type. That type does not exist yet: F1M2FE-T00 confirmed (A-3, re-run
 * 2026-10-14) the dev gateway serves none of this slice's R4 fields yet (`identityStatus` included,
 * 0 hits in `src/gql/schema.graphql`), and BE has not executed any task in this workflow (BE
 * workflow state `PLAN`). So there is nothing for `pnpm run codegen:dev` to generate against.
 *
 * This file defines a local literal union, `IdentityStatus`, with exactly the five BE-named values
 * the PRD (FR-F3) and DD give. When BE R4 ships and codegen produces the real
 * `NaturalPersonIdentityStatus` enum, swap this type alias for the generated one — the member set
 * is identical by construction, so the `Record` below stays exhaustive with no other change.
 */
export type IdentityStatus =
  | 'LINKED'
  | 'NO_EMAIL_PENDING'
  | 'CONFLICT_PENDING'
  | 'NO_EMAIL_ARCHIVED'
  | 'UNLINKED'

export interface IdentityStatusMeta {
  /** Text label — PRD FR-F3 requires a label, not colour alone. */
  label: string
  /**
   * True for the two statuses FR-F3/FR-16(a,e) block: the person cannot be given new
   * appointments, worker records, contacts or memberships until resolved. Drives
   * `IdentityBlockedBanner` (shown only for `NO_EMAIL_PENDING` and `CONFLICT_PENDING`).
   */
  blocked: boolean
}

/** Exhaustive by construction — `Record<IdentityStatus, …>` fails to compile if a member is missing. */
export const IDENTITY_STATUS_LABEL: Record<IdentityStatus, IdentityStatusMeta> = {
  LINKED: { label: 'Linked', blocked: false },
  NO_EMAIL_PENDING: { label: 'No email', blocked: true },
  CONFLICT_PENDING: { label: 'Shared email conflict', blocked: true },
  NO_EMAIL_ARCHIVED: { label: 'Archived (no email)', blocked: false },
  UNLINKED: { label: 'Not linked', blocked: false },
}

/** The five declared keys, in registry order — for enum-coverage guards/fixtures/stories. */
export const IDENTITY_STATUSES = Object.keys(IDENTITY_STATUS_LABEL) as IdentityStatus[]
